import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useReveal } from "../../hooks/useReveal";
import { easing } from "../../lib/animations";

/**
 * HomeCTASection — full-bleed brutalist CTA banner that lives between the
 * collection product grid and the community marquee on the home page.
 *
 * Visual: warm-white panel with a single oversized headline + a magnetic-style
 * arrow CTA. Designed to direct the eye toward the live drop without
 * shouting — quiet luxury, not a hype banner.
 */
export const HomeCTASection = () => {
    const [ref, inView] = useReveal({ threshold: 0.18 });

    return (
        <section
            ref={ref}
            data-testid="home-cta-section"
            style={{
                position: "relative",
                background: "#f1eee6",
                color: "#0c0c0c",
                padding: "clamp(64px, 9vw, 140px) clamp(28px, 5vw, 88px)",
                overflow: "hidden",
            }}
        >
            {/* Brutalist eyebrow rail */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                transition={{ duration: 0.7, ease: easing.primary }}
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 11,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    color: "rgba(12,12,12,0.55)",
                    marginBottom: "clamp(36px, 5vw, 64px)",
                }}
            >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                    <motion.span
                        aria-hidden="true"
                        animate={{ opacity: [1, 0.2, 1] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        style={{
                            width: 6,
                            height: 6,
                            background: "#c1121f",
                            display: "inline-block",
                            boxShadow: "0 0 4px rgba(193,18,31,0.55)",
                        }}
                    />
                    Section [05] · field active
                </span>
                <span>Proof — Field 002</span>
            </motion.div>

            {/* Headline */}
            <motion.h2
                initial={{ opacity: 0, y: 24 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                transition={{ duration: 1.0, ease: easing.primary, delay: 0.12 }}
                style={{
                    margin: 0,
                    fontFamily: "'Space Grotesk', Manrope, sans-serif",
                    fontWeight: 500,
                    fontSize: "clamp(40px, 7.5vw, 124px)",
                    lineHeight: 0.96,
                    letterSpacing: "-0.04em",
                    maxWidth: "14ch",
                }}
            >
                Built quiet. <br />
                Wear&nbsp;
                <span
                    style={{
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontWeight: 400,
                        color: "#3a3a3a",
                    }}
                >
                    loud.
                </span>
            </motion.h2>

            {/* CTA row */}
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                transition={{ duration: 0.9, ease: easing.primary, delay: 0.32 }}
                style={{
                    marginTop: "clamp(40px, 5vw, 72px)",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 28,
                    borderTop: "1px solid rgba(12,12,12,0.14)",
                    paddingTop: "clamp(20px, 3vw, 36px)",
                }}
            >
                <p
                    style={{
                        margin: 0,
                        maxWidth: "44ch",
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: "clamp(15px, 1.25vw, 19px)",
                        color: "#3a3a3a",
                        lineHeight: 1.55,
                    }}
                >
                    Eight artifacts. One drop. Heavy cotton, honest construction,
                    cut and washed in Warsaw. No restock when the field closes.
                </p>

                <div style={{ display: "inline-flex", gap: 22, flexWrap: "wrap" }}>
                    <Link
                        to="/shop"
                        data-testid="home-cta-shop"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "16px 28px",
                            border: "1px solid #0c0c0c",
                            color: "#f1eee6",
                            background: "#0c0c0c",
                            textDecoration: "none",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            transition: "transform 0.4s cubic-bezier(0.65, 0, 0.35, 1), background 0.4s ease, color 0.4s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-3px)";
                            e.currentTarget.style.background = "#c1121f";
                            e.currentTarget.style.borderColor = "#c1121f";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.background = "#0c0c0c";
                            e.currentTarget.style.borderColor = "#0c0c0c";
                        }}
                    >
                        Enter Lookbook
                        <svg width="22" height="9" viewBox="0 0 22 9" fill="none" aria-hidden="true">
                            <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="currentColor" />
                            <path d="M16 1L20 4.5L16 8" stroke="currentColor" fill="none" />
                        </svg>
                    </Link>
                    <Link
                        to="/drop/field-002"
                        data-testid="home-cta-drop"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 14,
                            padding: "16px 28px",
                            border: "1px solid #0c0c0c",
                            color: "#0c0c0c",
                            background: "transparent",
                            textDecoration: "none",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            transition: "transform 0.4s cubic-bezier(0.65, 0, 0.35, 1), background 0.4s ease, color 0.4s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-3px)";
                            e.currentTarget.style.background = "#0c0c0c";
                            e.currentTarget.style.color = "#f1eee6";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color = "#0c0c0c";
                        }}
                    >
                        Drop · Field 002
                    </Link>
                </div>
            </motion.div>
        </section>
    );
};

export default HomeCTASection;
